import { ClearOutlined, FilterOutlined } from '@ant-design/icons'
import { Button, Spin, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { useGeneral } from '@contexts'
import { useMaterials, useSuppliers } from '@hooks'

import { useFiltersV2 } from '../../contexts/FiltersContextV2'
import useContractsSupplier from '../../hooks/useContractsSupplier'
import {
  ContentContainer,
  StyledButton,
  StyledInput,
  StyledRow,
  StyledSelect,
} from '../StyledComponents'

const ContractsDashboardSupplier = () => {
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { suppliersOptionsSapCode } = useSuppliers()
  const { filters, handleResetFilters, handleFilterChange } = useFiltersV2()
  const { t } = useTranslation()
  const { contractsSupplier, contractsSupplierIsLoading } = useContractsSupplier(filters)
  const { materialsOptions } = useMaterials()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const columns = [
    {
      title: t('contract_dashboard.contract'),
      dataIndex: 'DocumentNumberID',
      key: 'DocumentNumberID',
    },
    {
      title: t('contract_dashboard.purchaser'),
      dataIndex: 'BuyerDesc',
      key: 'BuyerDesc',
    },
    {
      title: t('contract_dashboard.supplier'),
      dataIndex: 'supplier',
      key: 'tradeName',
      aligm: 'center',
      render: (supplier: { tradeName: any }) =>
        supplier?.tradeName || <span style={{ display: 'block', textAlign: 'center' }}>-</span>,
    },
    {
      title: t('contract_dashboard.header_plant'),
      key: 'headerPlant',
      render: (_: any, record: any) => `${record.PlantDesc || ''} / ${record.PlantID || ''}`,
    },
    {
      title: t('contract_dashboard.position'),
      dataIndex: 'Position',
      key: 'position',
      aligm: 'center',
    },
    {
      title: t('contract_dashboard.material'),
      dataIndex: 'MaterialDesc',
      key: 'nameShort',
    },
    {
      title: t('contract_dashboard.requested_quantity'),
      dataIndex: 'RequestedQuantity',
      key: 'RequestedQuantity',
    },
    {
      title: t('contract_dashboard.entry_quantity'),
      dataIndex: 'EntryQuantity',
      key: 'EntryQuantity',
    },
    {
      title: t('contract_dashboard.inco1_inco2'),
      key: 'inco',
      render: (_: any, record: any) => `${record.Inco1 || ''} / ${record.Inco2 || ''}`,
    },
    {
      title: t('contract_dashboard.date_validity_agreemen_from'),
      dataIndex: 'dateValidityAgreementFrom',
      key: 'dateValidityAgreementFrom',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD') : '-'),
    },
    {
      title: t('contract_dashboard.date_validity_agreemen_to'),
      dataIndex: 'dateValidityAgreementTo',
      key: 'dateValidityAgreementTo',
      render: (field: Date) => (field ? dayjs(field).format('YYYY/MM/DD') : '-'),
    },
  ]

  return (
    <ContentContainer className="admin-dashboard">
      <StyledRow className="sticky-options border-botom">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <Tooltip placement="bottomRight" title={t('globals.generics.clean_filters')}>
              <StyledButton
                className="inline option"
                onClick={handleResetFilters}
                icon={<ClearOutlined style={{ fontSize: '18px' }} />}
              />
            </Tooltip>
            <StyledSelect
              maxTagCount="responsive"
              showSearch
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.supplier')}
              options={suppliersOptionsSapCode}
              filterOption={handleFilterOptions}
              value={filters?.supplierSapCode}
              onChange={handleFilterChange('supplierSapCode')}
            />
            <StyledInput
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.purchase_order')}
              value={filters?.documentNumberId as string}
              onChange={({ target: { value } }) => {
                handleFilterChange('documentNumberId')(value)
              }}
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              placeholder={t('admin_router.filter_options.contract_filters.material')}
              options={materialsOptions}
              filterOption={handleFilterOptions}
              onChange={handleFilterChange('materialId')}
              value={filters?.materialId}
            />
          </div>
        </div>
      </StyledRow>
      <Spin spinning={contractsSupplierIsLoading}>
        <StyledRow className="data-table-container">
          <Table
            dataSource={contractsSupplier?.contracts}
            className="dataTable"
            columns={columns}
            pagination={{
              pageSize: filters.limit as number,
              onChange: handleFilterChange('page', false),
              total: contractsSupplier?.total,
              showSizeChanger: false,
              current: filters.page as number,
            }}
            showSorterTooltip={false}
            rowKey="id"
            scroll={{ x: true }}
            loading={contractsSupplierIsLoading}
            style={{ width: '100%', height: '100%' }}
          />
        </StyledRow>
      </Spin>
    </ContentContainer>
  )
}

export { ContractsDashboardSupplier }
